import * as THREE from "three";
import gsap from "gsap";
import Environment from "./Environment";
import SequenceBG from "./SequenceBG";
import Experience from "../Experience";
import CouplesRotation from "./CouplesRotation";
import TextCard from "./TextCard";
import VideoTextDom from "./VideoTextDom";
import EndCurtain, { OPEN_DURATION } from "./EndCurtain";
import EpisodeSlider from "./EpisodeSlider";

export default class World {
  constructor(){
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.sizes = this.experience.sizes;
    this.camera = this.experience.camera;
    this.sceneData = this.experience.sceneData;

    this.environment = new Environment();
    this.sequenceBG = new SequenceBG();
    this.couplesRotation = new CouplesRotation();
    this.textCard = new TextCard();
    // "Video Text" is now a real HTML/CSS card (see VideoTextDom) matching
    // the Figma design, not the reference's static Text.png backdrop. In
    // the reference, "Video" was a separate plain looping-video plane
    // stacked directly BEHIND "Video Text" (identical anims, near-identical
    // corner-pin frame — confirmed in preset.json/scene.json), with
    // Text.png's rounded frame masking it into the card. VideoTextDom's own
    // <video> now provides that same content directly, so a second "Video"
    // instance would just duplicate the same clip, unclipped, showing
    // through around the DOM card's rounded corners — dropped entirely.
    this.videoTextDom = new VideoTextDom();

    // Clip-boundary stops on the shared playhead (0, 140, 257, 360) — wheel
    // and touch step the playhead between these, one clip at a time.
    this.navStops = [0, ...this.sceneData.clips.map((c) => c.to)];
    this.currentStop = 0;
    this.targetPlayhead = 0;
    this.scrollLocked = false;

    // Stop 0 (the sky intro, frames 0-140) plays automatically on load —
    // no scroll input needed to reach the couples-rotation reveal. Once
    // that auto-play lands, floorStop pins the lowest stop scroll can
    // return to, so the intro can't be scrolled back into.
    this.floorStop = 0;

    // One-shot finale trigger — fires once the playhead reaches the last
    // navStop (the baked timeline's own final, black frame). finaleActive
    // additionally gates the old wheel/touch listeners off (see
    // setScrollListeners) once EpisodeSlider is mounted and owns input.
    this.finaleTriggered = false;
    this.finaleActive = false;

    this.init();
  }

  async init(){
    await this.sequenceBG.warmup();
    this.sequenceBG.createBGMesh();

    this.introRevealed = false;

    this.setScrollListeners();
    this.scrollToStop(1);
  }

  scrollToStop(index){
    const clamped = THREE.MathUtils.clamp(index, this.floorStop, this.navStops.length - 1);
    if(clamped === this.currentStop) return;

    this.currentStop = clamped;
    this.targetPlayhead = this.navStops[clamped];
    this.scrollLocked = true;
  }

  setScrollListeners(){
    window.addEventListener("wheel", (event) => {
      if(this.finaleActive) return; // EpisodeSlider owns input from here on
      if(!this.scrollLocked) {
        if(event.deltaY > 0) {
          this.scrollToStop(this.currentStop + 1);
        } else {
          this.scrollToStop(this.currentStop - 1);
        }
      }
    })

    const swipeThreshold = 30;
    let touchStartY = 0;

    window.addEventListener("touchstart", (event) => {
      touchStartY = event.touches[0].clientY;
    }, { passive: true });

    window.addEventListener("touchend", (event) => {
      if(this.finaleActive) return; // EpisodeSlider owns input from here on
      if(this.scrollLocked) return;

      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;

      if(Math.abs(deltaY) < swipeThreshold) return;

      if(deltaY > 0) {
        this.scrollToStop(this.currentStop + 1);
      } else {
        this.scrollToStop(this.currentStop - 1);
      }
    }, { passive: true });
  }

  // Advances experience.playhead toward targetPlayhead one frame per tick
  // (matches the previous currentIndex++/-- pacing), and fires the
  // sky-clip-reached moment once, the first time the playhead arrives.
  updatePlayhead(){
    const experience = this.experience;

    if(experience.playhead !== this.targetPlayhead) {
      if(experience.playhead < this.targetPlayhead) {
        experience.playhead++;
      } else {
        experience.playhead--;
      }
    } else if(this.scrollLocked) {
      this.scrollLocked = false;
    }

    if(!this.introRevealed && experience.playhead >= this.navStops[1]) {
      this.introRevealed = true;
      // Past the intro: raise the floor so scroll can no longer return to
      // stop 0 (the sky clip), matching the reference's one-way gate.
      this.floorStop = 1;
      this.couplesRotation.showCouplesRotation();
    }

    // The baked timeline's own final frame (navStops' last entry, =360,
    // the explode clip's end) is black by design — the handoff point into
    // the finale (camera settle -> curtain -> episode slider). Fires once,
    // automatically, no further scroll needed (confirmed with the user).
    const lastStop = this.navStops[this.navStops.length - 1];
    if(!this.finaleTriggered && experience.playhead >= lastStop) {
      this.finaleTriggered = true;
      this.triggerFinale();
    }
  }

  // Camera settle (stop tilt/mouse-sway, reusing Camera's own existing
  // tiltGain ease-to-0 — no new interpolation needed, see Camera.update)
  // -> curtain closes over the last baked frame -> curtain holds briefly
  // (long enough to actually read as "closed" before it moves) -> curtain
  // opens -> episode slider reveals. EpisodeSlider is constructed and
  // populated BEFORE the curtain even appears; since the curtain is about
  // to cover the whole viewport opaquely, mounting it early is exactly
  // "the scene behind them changed before they open" with no separate
  // swap step.
  triggerFinale(){
    this.finaleActive = true;
    this.camera.tilt.on = false;
    this.camera.circular.on = false;

    this.episodeSlider = new EpisodeSlider();
    // Makes the slider's root (and its zoomed-in background image) visible
    // NOW, while the curtain is about to fully cover the screen — so when
    // the curtain later parts, it reveals THIS, not the old WebGL scene
    // still sitting underneath (which is what was happening before: the
    // root stayed at opacity 0 until reveal(), called only after the
    // curtain finished opening, so there was a beat where the curtain
    // parted onto the stale frame-360 scene instead of the slider).
    this.episodeSlider.show();

    this.endCurtain = new EndCurtain();
    this.endCurtain.close();
    gsap.delayedCall(0.8, () => {
      // Zoom-out starts in lockstep with the curtain's own opening tween
      // (same duration) so the two motions read as one continuous reveal
      // instead of "curtain opens, THEN separately the image zooms".
      this.episodeSlider.beginZoomOut(OPEN_DURATION);
      this.endCurtain.open(() => {
        this.episodeSlider.reveal();
      });
    });
  }

  update(dt){
    const debug = this.experience.debug;

    debug?.begin("sequenceBG");
    this.sequenceBG.update(dt, this.experience.playhead);
    debug?.end("sequenceBG");

    debug?.begin("couplesRotation");
    this.couplesRotation.update(dt, this.experience.playhead);
    debug?.end("couplesRotation");

    debug?.begin("cards");
    this.textCard.update(dt, this.experience.playhead);
    this.videoTextDom.update(dt, this.experience.playhead);
    debug?.end("cards");

    if(this.finaleActive) {
      debug?.begin("finale");
      this.endCurtain?.update(dt);
      this.episodeSlider?.update(dt);
      debug?.end("finale");
    }
  }

  resize(){
    this.sequenceBG.resize();
    this.episodeSlider?.resize();
  }
}